import { useEffect, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

type GlbPreviewProps = {
  bodyCenter?: number[];
  directions?: Record<string, number[]>;
  emptyLabel?: string | null;
  glbUrl: string | null;
  isPlaying?: boolean;
  playbackTime?: number;
  playbackSpeed?: number;
  onDurationChange?: (duration: number) => void;
  onError: (message: string) => void;
  onReady: () => void;
  onTimeUpdate?: (time: number) => void;
};

export function GlbPreview({
  bodyCenter,
  directions,
  emptyLabel = "等待模型文件",
  glbUrl,
  isPlaying = false,
  playbackSpeed = 1,
  playbackTime,
  onDurationChange,
  onError,
  onReady,
  onTimeUpdate,
}: GlbPreviewProps) {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const mixerRef = useRef<THREE.AnimationMixer | null>(null);
  const durationRef = useRef(0);
  const isPlayingRef = useRef(isPlaying);
  const playbackSpeedRef = useRef(playbackSpeed);
  const onDurationChangeRef = useRef(onDurationChange);
  const onErrorRef = useRef(onError);
  const onReadyRef = useRef(onReady);
  const onTimeUpdateRef = useRef(onTimeUpdate);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    playbackSpeedRef.current = playbackSpeed;
  }, [playbackSpeed]);

  useEffect(() => {
    onTimeUpdateRef.current = onTimeUpdate;
  }, [onTimeUpdate]);

  useEffect(() => {
    onDurationChangeRef.current = onDurationChange;
  }, [onDurationChange]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);

  useEffect(() => {
    if (typeof playbackTime !== "number" || !Number.isFinite(playbackTime)) return;
    const mixer = mixerRef.current;
    if (!mixer) return;
    const duration = durationRef.current;
    mixer.setTime(duration > 0 ? clamp(playbackTime, 0, duration) : Math.max(0, playbackTime));
  }, [playbackTime]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount || !glbUrl) return;

    if (!hasWebGL()) {
      onErrorRef.current("当前 WebView 不支持 WebGL，无法渲染 GLB");
      return;
    }

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: "high-performance"
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x101512);

    const camera = new THREE.PerspectiveCamera(45, mount.clientWidth / mount.clientHeight, 0.01, 100);
    camera.position.set(0, 1.4, 3.2);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.enablePan = true;
    controls.minDistance = 0.6;
    controls.maxDistance = 8;

    scene.add(new THREE.HemisphereLight(0xffffff, 0x24382c, 2.1));
    const keyLight = new THREE.DirectionalLight(0xffffff, 2);
    keyLight.position.set(3, 5, 4);
    scene.add(keyLight);

    let mixer: THREE.AnimationMixer | null = null;
    let disposed = false;
    const clock = new THREE.Clock();
    mixerRef.current = null;
    durationRef.current = 0;

    const loader = new GLTFLoader();
    loader.load(
      glbUrl,
      (gltf) => {
        if (disposed) return;

        const model = gltf.scene;
        scene.add(model);
        fitCameraToObject(camera, controls, model, bodyCenter, directions);

        if (gltf.animations.length > 0) {
          mixer = new THREE.AnimationMixer(model);
          mixerRef.current = mixer;
          durationRef.current = Math.max(...gltf.animations.map((clip) => clip.duration), 0);
          onDurationChangeRef.current?.(durationRef.current);
          gltf.animations.forEach((clip) => {
            const action = mixer?.clipAction(clip);
            action?.play();
          });
        } else {
          onDurationChangeRef.current?.(0);
        }

        onReadyRef.current();
      },
      undefined,
      (error) => {
        console.error(error);
        onErrorRef.current("GLB 加载失败，可能是文件损坏或 WebView 不支持当前模型");
      }
    );

    function animate() {
      if (disposed) return;
      requestAnimationFrame(animate);
      const delta = clock.getDelta();
      if (mixer && isPlayingRef.current) {
        mixer.update(delta * playbackSpeedRef.current);
        const duration = durationRef.current;
        if (duration > 0) {
          const time = mixer.time % duration;
          onTimeUpdateRef.current?.(time);
        }
      }
      controls.update();
      renderer.render(scene, camera);
    }

    function resize() {
      if (!mount) return;
      const width = mount.clientWidth;
      const height = mount.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    }

    window.addEventListener("resize", resize);
    animate();

    return () => {
      disposed = true;
      window.removeEventListener("resize", resize);
      controls.dispose();
      renderer.dispose();
      mixerRef.current = null;
      durationRef.current = 0;
      scene.traverse((object) => {
        const mesh = object as THREE.Mesh;
        mesh.geometry?.dispose?.();
        const material = mesh.material;
        if (Array.isArray(material)) {
          material.forEach((item) => item.dispose());
        } else {
          material?.dispose?.();
        }
      });
      renderer.domElement.remove();
    };
  }, [bodyCenter, directions, glbUrl]);

  return (
    <div className="glb-preview" ref={mountRef}>
      {!glbUrl && emptyLabel ? <div className="empty-state">{emptyLabel}</div> : null}
    </div>
  );
}

function hasWebGL() {
  const canvas = document.createElement("canvas");
  return Boolean(canvas.getContext("webgl2") || canvas.getContext("webgl"));
}

function fitCameraToObject(
  camera: THREE.PerspectiveCamera,
  controls: OrbitControls,
  object: THREE.Object3D,
  bodyCenter?: number[],
  directions?: Record<string, number[]>
) {
  const box = new THREE.Box3().setFromObject(object);
  const size = box.getSize(new THREE.Vector3());
  const center = vectorFromArray(bodyCenter) ?? box.getCenter(new THREE.Vector3());
  const maxSize = Math.max(size.x, size.y, size.z) || 1;
  const fitHeightDistance = maxSize / (2 * Math.atan((Math.PI * camera.fov) / 360));
  const fitWidthDistance = fitHeightDistance / camera.aspect;
  const distance = Math.max(fitHeightDistance, fitWidthDistance) * 1.5;
  const front = vectorFromArray(directions?.front);
  const direction = front ? front.multiplyScalar(-1).normalize() : new THREE.Vector3(0, 0, 1).normalize();

  camera.position.copy(center).add(direction.multiplyScalar(distance));
  camera.position.y = center.y;
  camera.near = Math.max(distance / 100, 0.01);
  camera.far = distance * 100;
  camera.updateProjectionMatrix();

  controls.target.copy(center);
  controls.update();
}

function vectorFromArray(value?: number[]) {
  if (!value || value.length < 3) return null;
  const [x, y, z] = value;
  if (![x, y, z].every((item) => typeof item === "number" && Number.isFinite(item))) return null;
  return new THREE.Vector3(x, y, z);
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}
